import React from 'react';
import { classnames } from 'Utils/classnames/classnames.utils';
import { t } from 'Src/i18n';
import { capitalizeName } from 'Utils/label/labelUtils';
import { ICONS } from 'Components/utils/icon/icons';
import { Icon } from 'Components/frameworks/icon/Icon';
import { TRANSPORT_CLASS_TO_I18N } from 'Components/widgets/transport-details/transportUtils';
import { TransportDetailsTravelSectionBound } from './TransportDetailsTravelSectionBound';
import { TransportDetailsTravelSectionReference } from './TransportDetailsTravelSectionReference';
import { TransportDetailsTravelSectionStop } from './TransportDetailsTravelSectionStop';
type TransportDetailsTravelSectionProps = {
  firstDeparture: TravelSectionBound;
  hasRefNumberEnabled?: boolean;
  onClickCopy?: () => void;
  travelSection: TravelSection;
};

function computeTransportLabel(transport: TransportType) {
  const transportClass = transport.class
    ? `${t(`common.transport.transportClass.${TRANSPORT_CLASS_TO_I18N[transport.class]}`)} - `
    : '';
  const reference = `${transport.reference || transport.travelNumber || ''}`;
  const flightNumber = `${transport.companyOperator.id} ${reference}`;
  const companyName = capitalizeName(transport.companyOperator.label);
  const vehicleName = transport.vehicleType ? capitalizeName(transport.vehicleType) : null;
  return `${transportClass}${flightNumber} ${companyName}${vehicleName ? ` - ${vehicleName}` : ''}`;
}

const transportTypeToIcon = {
  TRAIN: ICONS.TRAIN,
  PLANE: ICONS.PLANE,
  BOAT: ICONS.BOAT,
};
export function TransportDetailsTravelSection({
  firstDeparture,
  hasRefNumberEnabled = false,
  onClickCopy,
  travelSection,
}: TransportDetailsTravelSectionProps) {
  const {
    arrival,
    attendees = [],
    connectionDurationInMin,
    departure,
    stopDurationInMin,
    technicalStops,
    transport,
  } = travelSection;
  const transportLabel = computeTransportLabel(travelSection.transport);
  const isTrain = transport.type === 'TRAIN';
  return (
    <div data-testid="TransportDetailsTravelSection">
      {stopDurationInMin && (
        <TransportDetailsTravelSectionStop type="stop" durationInMin={stopDurationInMin} />
      )}
      {connectionDurationInMin && (
        <TransportDetailsTravelSectionStop
          type="connection"
          durationInMin={connectionDurationInMin}
        />
      )}
      <div className="flex items-center gap-10">
        <span className="text-12 text-darkGrey">{transportLabel}</span>
        <Icon name={transportTypeToIcon[transport.type || 'PLANE']} width="1.5rem" />
      </div>
      <div className="ms-30 mt-10">
        <ul className="list-none no-underline">
          <li
            className={classnames('relative', {
              ['before:absolute before:left-[-25px] before:top-[7px] before:h-[11px] before:w-[11px] before:rounded-full before:border before:border-black before:content-[""]']:
                true,
              ['after:absolute after:left-[-20px] after:top-[17px] after:h-[calc(100%+10px)] after:border-l after:border-black after:content-[""]']:
                true,
            })}
          >
            <TransportDetailsTravelSectionBound
              isDeparture
              travelSectionBound={departure}
              firstDeparture={firstDeparture}
            />
            {technicalStops && technicalStops !== '0' && !isTrain && (
              <TransportDetailsTravelSectionStop type="technicalStop" />
            )}
          </li>
          <li
            className={classnames('relative mt-20', {
              ['before:absolute before:left-[-25px] before:top-[7px] before:h-[11px] before:w-[11px] before:rounded-full before:border before:border-black before:content-[""]']:
                true,
            })}
          >
            <TransportDetailsTravelSectionBound
              isDeparture={false}
              travelSectionBound={arrival}
              firstDeparture={firstDeparture}
            />
          </li>
        </ul>
      </div>
      {hasRefNumberEnabled && (
        <TransportDetailsTravelSectionReference
          attendees={attendees}
          className="mt-20"
          onClickCopy={onClickCopy}
          type={transport?.type}
        />
      )}
    </div>
  );
}
